// Password and passphrase generation. Pure functions over the CSPRNG helpers so
// they are easy to unit-test. No randomness source other than random.ts.
import { randomChoice, secureRandomInt, secureShuffle } from './random';

export interface CharacterOptions {
  length: number;
  lowercase: boolean;
  uppercase: boolean;
  digits: boolean;
  symbols: boolean;
  /** Drop visually ambiguous characters (0/O/o, 1/l/I, |). */
  excludeAmbiguous: boolean;
}

export interface PassphraseOptions {
  wordCount: number;
  /** Joins the words, e.g. '-', '.', or ' '. */
  separator: string;
  /** Capitalize the first letter of each word. */
  capitalize: boolean;
  /** Append a random digit at the end. */
  includeNumber: boolean;
}

export interface MemorableOptions {
  wordCount: number;
  /** Joins the words and the complexity tail, e.g. '-'. */
  separator: string;
}

const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!@#$%^&*()-_=+[]{};:,.<>?';
const AMBIGUOUS = new Set('O0oIl1|'.split(''));

export const PASSWORD_LENGTH_MIN = 8;
export const PASSWORD_LENGTH_MAX = 64;
export const PASSPHRASE_WORDS_MIN = 3;
export const PASSPHRASE_WORDS_MAX = 8;
export const MEMORABLE_WORDS_MIN = 2;
export const MEMORABLE_WORDS_MAX = 6;

/** Safe, easy-to-type symbols for the memorable complexity tail. */
export const MEMORABLE_SYMBOLS = '!?#$%&*+@';

function filterAmbiguous(set: string, exclude: boolean): string {
  if (!exclude) return set;
  return set
    .split('')
    .filter((c) => !AMBIGUOUS.has(c))
    .join('');
}

/** Builds the active character classes for the given options (after ambiguity filtering). */
export function activeClasses(opts: CharacterOptions): string[] {
  const classes: string[] = [];
  if (opts.lowercase) classes.push(filterAmbiguous(LOWERCASE, opts.excludeAmbiguous));
  if (opts.uppercase) classes.push(filterAmbiguous(UPPERCASE, opts.excludeAmbiguous));
  if (opts.digits) classes.push(filterAmbiguous(DIGITS, opts.excludeAmbiguous));
  if (opts.symbols) classes.push(filterAmbiguous(SYMBOLS, opts.excludeAmbiguous));
  return classes.filter((c) => c.length > 0);
}

/** Total size of the character pool — drives the entropy calculation. */
export function poolSize(opts: CharacterOptions): number {
  return activeClasses(opts).reduce((sum, c) => sum + c.length, 0);
}

/**
 * Generates a random character password. Guarantees at least one character from
 * each selected class (when length permits), then fills the rest from the full
 * pool and shuffles so the guaranteed characters are not positionally biased.
 */
export function generatePassword(opts: CharacterOptions): string {
  const classes = activeClasses(opts);
  if (classes.length === 0) {
    throw new Error('generatePassword: at least one character class must be enabled');
  }
  if (opts.length < 1) {
    throw new Error('generatePassword: length must be >= 1');
  }

  const pool = classes.join('');
  const chars: string[] = [];

  // One guaranteed character per class (only up to the requested length).
  for (const cls of classes) {
    if (chars.length < opts.length) {
      chars.push(randomChoice(cls.split('')));
    }
  }

  const poolChars = pool.split('');
  while (chars.length < opts.length) {
    chars.push(poolChars[secureRandomInt(poolChars.length)]);
  }

  return secureShuffle(chars).join('');
}

/**
 * Generates a passphrase from a word list. The list must be non-empty (the caller
 * lazy-loads it and handles load failures — see plan FINDING-10).
 */
export function generatePassphrase(words: readonly string[], opts: PassphraseOptions): string {
  if (words.length === 0) {
    throw new Error('generatePassphrase: word list is empty');
  }
  if (opts.wordCount < 1) {
    throw new Error('generatePassphrase: wordCount must be >= 1');
  }

  const picked: string[] = [];
  for (let i = 0; i < opts.wordCount; i++) {
    let word = randomChoice(words);
    if (opts.capitalize) {
      word = word.charAt(0).toUpperCase() + word.slice(1);
    }
    picked.push(word);
  }

  let phrase = picked.join(opts.separator);
  if (opts.includeNumber) {
    phrase += String(secureRandomInt(10));
  }
  return phrase;
}

/**
 * Memorable password: a few easy lowercase words plus a complexity tail of
 * digits + symbol + digits, e.g. `hallo-wish-unter-12!2383`. Meets the common
 * "needs a number and a special character" policy while staying readable.
 */
export function generateMemorable(words: readonly string[], opts: MemorableOptions): string {
  if (words.length === 0) {
    throw new Error('generateMemorable: word list is empty');
  }
  if (opts.wordCount < 1) {
    throw new Error('generateMemorable: wordCount must be >= 1');
  }

  const picked: string[] = [];
  for (let i = 0; i < opts.wordCount; i++) {
    picked.push(randomChoice(words).toLowerCase());
  }

  const twoDigits = String(secureRandomInt(90) + 10); // 10–99
  const symbol = randomChoice(MEMORABLE_SYMBOLS.split(''));
  const fourDigits = String(secureRandomInt(9000) + 1000); // 1000–9999
  const tail = `${twoDigits}${symbol}${fourDigits}`;

  return `${picked.join(opts.separator)}${opts.separator}${tail}`;
}
